import { useEffect, useState } from "react";
import { addDays, format } from "date-fns";
import { Building2, User as UserIcon } from "lucide-react";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { usePermissao } from "@/lib/use-permissao";
import { useConfirm } from "@/lib/confirm";
import { timeToMinutes, minutesToTime } from "@/lib/agenda-time";
import { OrganizationOriginBox } from "@/components/shared/OrganizationOriginBox";
import { CartaoTrocavel, TrocarPaciente } from "./CartaoTrocavel";
import { isUrgentDeadline } from "@/lib/prosthesis-urgency";
import { AgendaAppointmentDialog, type DialogInitial } from "@/components/agenda/AgendaAppointmentDialog";
import { ProsthesisLabAddDialog } from "./ProsthesisLabAddDialog";
import { ProsthesisTeethPicker } from "./ProsthesisTeethPicker";
import { ProsthesisSimpleServiceForm } from "./ProsthesisSimpleServiceForm";
import { ProsthesisOrderSummaryPanel } from "./ProsthesisOrderSummaryPanel";
import { ConfirmSendOrderDialog } from "./ConfirmSendOrderDialog";
import { FDI_LOWER_ARCH, FDI_UPPER_ARCH, formatarDentes, parseTeethToFdi } from "./fdi-teeth";
import type { ServiceDraft } from "./prosthesis-service-draft";
import type { OrderStart } from "./NewOrderStartDialog";
import type { ProsthesisOrderRow } from "./types";
import { somarDias, type EntregaDoPedido, type PrazoDoLaboratorio } from "@bookdental/schemas";
import { EnderecoDeEnvio, entregaCompleta } from "./EnderecoDeEnvio";
import { useEntregaPadrao } from "./use-entrega-padrao";
import { CampoDentistaResponsavel, usePrecisaEscolherDentista } from "./CampoDentistaResponsavel";

const inputCls =
  "h-9 w-full rounded-md border border-zinc-200 bg-white px-3 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:cursor-not-allowed disabled:bg-zinc-50 disabled:text-zinc-500 dark:border-border dark:bg-card dark:text-foreground dark:disabled:bg-muted";
const labelCls = "mb-1 block text-xs font-medium text-zinc-600 dark:text-muted-foreground";


export function ProsthesisRequestForm({
  start,
  onCreated,
  editing,
}: {
  start: OrderStart;
  onCreated: () => void;
  /** Pedido existente, só enquanto AGUARDANDO_CONFIRMACAO: o formulário abre preenchido e salvar
   *  substitui em vez de criar. Paciente e laboratório seguem travados — trocar laboratório é
   *  apagar e refazer (ver `prosthesis.update`). */
  editing?: ProsthesisOrderRow;
}) {
  const editando = !!editing;
  const utils = trpc.useUtils();
  const confirm = useConfirm();
  const labsQuery = trpc.prosthesis.lab.options.useQuery();
  const settingsQuery = trpc.settings.getAgenda.useQuery();
  const labs = labsQuery.data ?? [];
  // Paciente e laboratório trocam no próprio formulário (a clínica não: tudo depende dela).
  const [patient, setPatient] = useState(start.patient);
  const [trocando, setTrocando] = useState<"paciente" | "laboratorio" | null>(null);
  // Vindo do plano, o pedido fica ligado ao item daquele paciente; editando, o servidor não troca.
  const podeTrocarPaciente = !editando && !start.doPlano;
  // Só pra arcada abrir na decídua quando é criança — quem não vê pacientes fica no padrão.
  const podeVerPaciente = usePermissao("patients.view");
  const pacienteQuery = trpc.patient.getById.useQuery({ id: patient.id ?? "" }, { enabled: !!patient.id && podeVerPaciente });
  const nascimentoDoPaciente = pacienteQuery.data?.birthDate ?? null;

  const [labId, setLabId] = useState(start.labId);
  const [dentistaId, setDentistaId] = useState(editing?.dentistaId ?? "");
  const precisaDentista = usePrecisaEscolherDentista();
  const [labAddOpen, setLabAddOpen] = useState(false);
  const [followUpInitial, setFollowUpInitial] = useState<DialogInitial | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deadline, setDeadline] = useState(() => (editing?.deadline ? format(new Date(editing.deadline), "yyyy-MM-dd") : ""));
  // De onde veio o prazo: "lab" segue o prazo previsto do laboratório escolhido (e muda junto se
  // trocar de laboratório), "seis" é o atalho de seis dias, "manual" é o que a pessoa digitou e
  // não se mexe mais. Começa em "lab" pra o campo já vir preenchido — ver o efeito abaixo.
  // Editando, o prazo que já existe vale mais que qualquer sugestão — começa em "manual".
  const [deadlineMode, setDeadlineMode] = useState<PrazoModo>(editing ? "manual" : "lab");
  const [description, setDescription] = useState(editing?.description ?? "");
  const [services, setServices] = useState<ServiceDraft[]>(() => (editing ? rascunhosDoPedido(editing) : []));
  const [attachments, setAttachments] = useState<{ fileId: string; fileName: string }[]>([]);
  const [attachmentChannels, setAttachmentChannels] = useState<string[]>(editing?.sentChannels ?? []);
  // `null` = vai pro endereço padrão (o da clínica, ou o que o pedido em edição já tem).
  const [entrega, setEntrega] = useState<EntregaDoPedido | null>(null);
  const entregaPadrao = useEntregaPadrao(editing);
  const attachToOrder = trpc.file.attachToEntity.useMutation();

  // Dentes/região — compartilhado pelos dois modos (um odontograma só, uma seleção só).
  // Vindo do plano de tratamento, os dentes do item já chegam marcados (ou no campo livre).
  const dentesDoPlano = start.doPlano?.dentes ?? null;
  const [manualTeeth, setManualTeeth] = useState(() => (parseTeethToFdi(dentesDoPlano).length ? "" : (dentesDoPlano ?? "")));
  const [selectedFdi, setSelectedFdi] = useState<string[]>(() => parseTeethToFdi(dentesDoPlano));
  // O componente é não-controlado (só aceita seleção inicial via defaultSelected) —
  // trocar essa key força remontar com a seleção desejada (arcada, limpar, próximo serviço).
  const [odontogramKey, setOdontogramKey] = useState(0);

  const teethLocked = selectedFdi.length > 0;
  const isUpperArchSelected = FDI_UPPER_ARCH.every((f) => selectedFdi.includes(f));
  const isLowerArchSelected = FDI_LOWER_ARCH.every((f) => selectedFdi.includes(f));
  const isFullArchSelected = isUpperArchSelected && isLowerArchSelected;
  // Regra compartilhada com o editor do laboratório (`formatarDentes`): arcada inteira vira rótulo,
  // e não a lista de 32 dentes, que não cabe no campo.
  const teeth = teethLocked ? (formatarDentes(selectedFdi) ?? "") : manualTeeth;

  const toggleArch = (arch: string[], isSelected: boolean) => {
    setSelectedFdi((prev) =>
      isSelected ? prev.filter((f) => !arch.includes(f)) : Array.from(new Set([...prev, ...arch])),
    );
    setOdontogramKey((k) => k + 1);
  };

  const clearSelection = () => {
    setSelectedFdi([]);
    setOdontogramKey((k) => k + 1);
  };

  /** Serviço de categoria "Arcada Completa" preenche as duas arcadas sozinho — sem ação manual. */
  const selectFullArch = () => {
    setSelectedFdi(Array.from(new Set([...FDI_UPPER_ARCH, ...FDI_LOWER_ARCH])));
    setOdontogramKey((k) => k + 1);
  };

  /** Serviço de "Dente Único" desfaz a arcada completa auto-selecionada — dentista escolhe os
   *  dentes manualmente. Não mexe numa seleção manual parcial já feita antes. */
  const deselectFullArchIfActive = () => {
    if (isFullArchSelected) clearSelection();
  };

  const selectedLab = labs.find((l: { id: string }) => l.id === labId) as
    | { id: string; name: string; linkedOrganizationId: string | null; expectedDays: number | null; prazo: PrazoDoLaboratorio | null }
    | undefined;

  function handleAddService(draft: ServiceDraft) {
    setServices((prev) => [...prev, draft]);
    setManualTeeth("");
    clearSelection();
  }

  function removeService(index: number) {
    setServices((prev) => prev.filter((_, i) => i !== index));
  }

  // Os serviços são do catálogo do laboratório (material, cor, preço): trocando, eles saem.
  // Dentes, prazo, observações e anexos ficam.
  async function trocarLaboratorio(novo: string) {
    if (!novo || novo === labId) return setTrocando(null);
    if (services.length > 0) {
      const ok = await confirm({
        title: "Trocar o laboratório?",
        description: `${services.length === 1 ? "O serviço adicionado sai" : `Os ${services.length} serviços adicionados saem`} do pedido: ${services.length === 1 ? "ele é" : "eles são"} do catálogo de ${selectedLab?.name ?? "outro laboratório"}. Dentes, prazo, observações e anexos continuam.`,
        confirmText: "Trocar e tirar os serviços",
        cancelText: "Manter",
        destructive: true,
      });
      if (!ok) return;
      setServices([]);
    }
    setLabId(novo);
    setTrocando(null);
  }

  // "yyyy-MM-dd" lido por new Date() cai em UTC: no Brasil vira o dia anterior e acusava urgência
  // até no prazo do próprio laboratório.
  const isUrgent = isUrgentDeadline(deadline ? parseYmdLocal(deadline) : null, new Date(), selectedLab?.prazo);

  // Prazo pré-selecionado: o dentista quase nunca sabe de cabeça o prazo do laboratório, e o campo
  // vazio o obrigava a adivinhar (e a errar pra menos, virando urgência sem querer). Seis dias é o
  // outro hábito comum — o paciente costuma ser remarcado pra sete, e o trabalho precisa chegar
  // antes. Sem laboratório escolhido ainda, cai nos seis também.
  const prazoDoLab = selectedLab?.prazo ?? null;
  useEffect(() => {
    if (deadlineMode === "manual") return;
    const vence = deadlineMode === "lab" && prazoDoLab ? somarDias(new Date(), prazoDoLab.dias, prazoDoLab) : addDays(new Date(), SEIS_DIAS);
    setDeadline(format(vence, "yyyy-MM-dd"));
  }, [deadlineMode, prazoDoLab]);

  const update = trpc.prosthesis.update.useMutation({
    onSuccess: (order) => {
      utils.prosthesis.list.invalidate();
      utils.prosthesis.porAutor.invalidate();
      utils.prosthesis.getById.invalidate({ id: order.id });
      utils.prosthesis.countByStatus.invalidate();
      toast.success("Pedido atualizado.");
      onCreated();
    },
    onError: (e) => toast.error(e.message || "Erro ao salvar o pedido."),
  });

  const create = trpc.prosthesis.create.useMutation({
    onSuccess: async (order) => {
      utils.prosthesis.list.invalidate();
      utils.prosthesis.porAutor.invalidate();
      utils.prosthesis.countByStatus.invalidate();
      utils.patient.list.invalidate();
      if (start.doPlano) utils.plano.invalidate();
      setConfirmOpen(false);
      toast.success("Pedido enviado com sucesso.");

      for (const anexo of attachments) {
        attachToOrder.mutate({
          fileId: anexo.fileId,
          relatedType: "PROSTHESIS_ORDER",
          relatedId: order.id,
        });
      }

      const wantsFollowUp = await confirm({
        title: "Criar consulta de retorno?",
        description: `Deseja agendar automaticamente uma consulta de retorno para ${patient.name}?`,
        confirmText: "Sim, agendar",
        cancelText: "Não agora",
      });

      if (wantsFollowUp && settingsQuery.data) {
        const s = settingsQuery.data;
        const startMin = timeToMinutes(s.startHour);
        const endMin = Math.min(startMin + s.defaultAppointmentDuration, timeToMinutes(s.endHour));
        // Sugestão de data: 1 dia após o prazo do pedido (ou amanhã, se o pedido não tiver
        // prazo definido) — o dentista ainda escolhe a data final no diálogo que abre a seguir.
        const followUpDate = deadline ? addDays(parseYmdLocal(deadline), 1) : addDays(new Date(), 1);
        setFollowUpInitial({
          date: followUpDate,
          startTime: s.startHour,
          endTime: minutesToTime(endMin),
          // patient.id local pode estar vazio (walk-in) — o pedido já cadastrou o paciente de
          // verdade, então usa o patientId que voltou da criação pra não duplicar o cadastro.
          patientId: order.patientId ?? patient.id,
          patientName: patient.name,
          patientPhone: patient.phone,
        });
      } else {
        resetForm();
        onCreated();
      }
    },
    onError: (e) => toast.error(e.message || "Erro ao enviar pedido."),
  });

  function finishAfterFollowUp() {
    setFollowUpInitial(null);
    resetForm();
    onCreated();
  }

  function resetForm() {
    setDeadline("");
    setDescription("");
    setServices([]);
    setManualTeeth("");
    setAttachments([]);
    setAttachmentChannels([]);
    clearSelection();
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (patient.name.trim().length < 2) {
      toast.error("Informe o paciente.");
      return;
    }
    if (services.length === 0) {
      toast.error("Adicione ao menos um serviço ao pedido.");
      return;
    }
    if (precisaDentista && !dentistaId) {
      toast.error("Escolha o dentista responsável pelo pedido.");
      return;
    }
    if (!editando && !entrega && !entregaPadrao) {
      toast.error("A clínica ainda não tem endereço cadastrado — informe o endereço de envio.");
      return;
    }
    if (entrega && !entregaCompleta(entrega)) {
      toast.error("Complete o endereço de envio — só o complemento é opcional.");
      return;
    }
    // Editar não passa pela confirmação de envio: o pedido já foi enviado, isto só corrige.
    if (editando) {
      submitOrder();
      return;
    }
    if (!labId) {
      toast.error("Selecione um laboratório antes de enviar o pedido.");
      return;
    }
    setConfirmOpen(true);
  }

  function submitOrder() {
    const comum = {
      patientId: patient.id ?? undefined,
      patientName: patient.name.trim(),
      patientPhone: patient.phone.replace(/\D/g, "") || undefined,
      deadline: deadline ? parseYmdLocal(deadline) : undefined,
      description: description || undefined,
      sentChannels: attachmentChannels,
      entrega: entrega ?? undefined,
      dentistaId: precisaDentista ? dentistaId : undefined,
      services: services.map((s) => ({
        serviceCategoryId: s.serviceCategoryId,
        materialOptionId: s.materialOptionId ?? undefined,
        shadeOptionId: s.shadeOptionId ?? undefined,
        translucencyOptionId: s.translucencyOptionId ?? undefined,
        blockSizeId: s.blockSizeId ?? undefined,
        manufacturingProcessId: s.manufacturingProcessId ?? undefined,
        characterizationOptionId: s.characterizationOptionId ?? undefined,
        finishingOptionId: s.finishingOptionId ?? undefined,
        teeth: s.teeth || undefined,
        quantidade: s.quantidade,
        notes: s.notes ?? undefined,
        attachmentFileId: s.attachmentFileId ?? undefined,
      })),
    };
    if (editing) update.mutate({ orderId: editing.id, ...comum });
    else create.mutate({ ...comum, labId: labId || undefined, itemDoPlanoId: start.doPlano?.itemId });
  }

  return (
    <div className="flex w-full flex-col gap-4">
      <div className="grid gap-3 sm:grid-cols-3">
        <OrganizationOriginBox />
        <CartaoTrocavel
          icon={UserIcon}
          label="Paciente"
          value={patient.name}
          trocando={trocando === "paciente"}
          onTrocar={podeTrocarPaciente ? () => setTrocando("paciente") : undefined}
          onCancelar={() => setTrocando(null)}
        >
          <TrocarPaciente
            onEscolher={(p) => {
              setPatient(p);
              setTrocando(null);
            }}
          />
        </CartaoTrocavel>
        {labId ? (
          <CartaoTrocavel
            icon={Building2}
            label="Laboratório"
            value={selectedLab?.name ?? "Laboratório"}
            trocando={trocando === "laboratorio"}
            onTrocar={editando || labs.length < 2 ? undefined : () => setTrocando("laboratorio")}
            onCancelar={() => setTrocando(null)}
          >
            <select value={labId} onChange={(e) => trocarLaboratorio(e.target.value)} autoFocus className={inputCls}>
              {labs.map((l: { id: string; name: string }) => (
                <option key={l.id} value={l.id}>
                  {l.name}
                </option>
              ))}
            </select>
          </CartaoTrocavel>
        ) : (
          <div className="flex flex-col gap-1.5 rounded-lg border border-amber-200 bg-amber-50/60 px-3 py-2 dark:border-amber-500/30 dark:bg-amber-500/10">
            <label className="text-xs font-semibold uppercase tracking-wider text-amber-700 dark:text-amber-400">
              Laboratório — obrigatório pra enviar
            </label>
            <select value={labId} onChange={(e) => setLabId(e.target.value)} className={inputCls}>
              <option value="">Selecione</option>
              {labs.map((l: { id: string; name: string }) => (
                <option key={l.id} value={l.id}>
                  {l.name}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => setLabAddOpen(true)}
              className="self-start text-[11px] font-medium text-brand-600 hover:underline dark:text-primary"
            >
              Buscar novo laboratório
            </button>
          </div>
        )}
      </div>

      <CampoDentistaResponsavel valor={dentistaId} onChange={setDentistaId} />

      <EnderecoDeEnvio valor={entrega} onChange={setEntrega} padrao={entregaPadrao} />

      <div className="flex w-full flex-col gap-4 lg:flex-row lg:items-start">
        <ProsthesisTeethPicker
          selectedFdi={selectedFdi}
          odontogramKey={odontogramKey}
          onChange={setSelectedFdi}
          onToggleArch={toggleArch}
          nascimento={nascimentoDoPaciente}
        />

        <form
          id="prosthesis-order-form"
          onSubmit={handleSubmit}
          className="flex-1 space-y-5 rounded-xl border border-zinc-200 bg-white p-5 shadow-sm dark:border-border dark:bg-card sm:p-6"
        >
          <div className="space-y-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-zinc-400 dark:text-muted-foreground">
              Adicionar serviço
            </p>

            <div>
              <label className={labelCls}>Dentes / região *</label>
              <input
                value={teeth}
                onChange={(e) => setManualTeeth(e.target.value)}
                disabled={teethLocked}
                placeholder="Ex: 11, 21"
                className={inputCls}
              />
              <p className="mt-1.5 flex items-center justify-between text-[11px] text-zinc-500 dark:text-muted-foreground">
                <span>
                  {teethLocked
                    ? "Selecionado pelo odontograma ao lado."
                    : "Digite livremente ou selecione no odontograma ao lado."}
                </span>
                {teethLocked && (
                  <button type="button" onClick={clearSelection} className="font-medium text-brand-600 hover:underline dark:text-primary">
                    Limpar seleção
                  </button>
                )}
              </p>
            </div>

            <ProsthesisSimpleServiceForm
              // Trocar de laboratório recomeça a escolha: o serviço em montagem era do catálogo do outro.
              key={labId}
              teeth={teeth}
              labOrganizationId={selectedLab?.linkedOrganizationId ?? undefined}
              inicial={editing || labId !== start.labId ? undefined : start.servico}
              onAdd={handleAddService}
              onFullArchSelected={selectFullArch}
              onSingleToothServiceSelected={deselectFullArchIfActive}
            />
          </div>
        </form>

        <ProsthesisOrderSummaryPanel
          services={services}
          onRemove={removeService}
          isPending={create.isPending || update.isPending}
          modo={editando ? "editar" : "criar"}
          deadline={deadline}
          onDeadlineChange={(v) => {
            setDeadlineMode("manual");
            setDeadline(v);
          }}
          deadlineMode={deadlineMode}
          onDeadlinePreset={setDeadlineMode}
          description={description}
          onDescriptionChange={setDescription}
          prazo={prazoDoLab}
          isUrgent={isUrgent}
          attachments={attachments}
          onAttachmentsChange={setAttachments}
          attachmentChannels={attachmentChannels}
          onAttachmentChannelsChange={setAttachmentChannels}
        />
      </div>

      <ConfirmSendOrderDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={submitOrder}
        isPending={create.isPending}
        patientName={patient.name}
        labName={selectedLab?.name ?? null}
        services={services}
        deadline={deadline}
        description={description}
        isUrgent={isUrgent}
        sentChannels={attachmentChannels}
      />

      <ProsthesisLabAddDialog open={labAddOpen} onClose={() => setLabAddOpen(false)} />

      {followUpInitial && settingsQuery.data && (
        <AgendaAppointmentDialog
          open
          mode="create"
          initial={followUpInitial}
          settings={settingsQuery.data}
          onClose={finishAfterFollowUp}
          onSaved={finishAfterFollowUp}
        />
      )}
    </div>
  );
}

const SEIS_DIAS = 6;

/** Serviços gravados de volta pra rascunho do formulário — o inverso do que `submitOrder` faz. */
function rascunhosDoPedido(order: ProsthesisOrderRow): ServiceDraft[] {
  return order.services.map((s) => ({
    serviceCategoryId: s.serviceCategory.id,
    serviceCategoryLabel: s.serviceCategory.label,
    materialOptionId: s.materialOption?.id ?? null,
    // O rótulo congelado vem primeiro: se o laboratório renomeou a oferta depois, o rascunho
    // tem que mostrar o que foi pedido, não o nome novo.
    materialOptionLabel: s.materialOptionLabel ?? s.materialOption?.label ?? null,
    shadeOptionId: s.shadeOption?.id ?? null,
    shadeOptionLabel: s.shadeOption?.code ?? null,
    translucencyOptionId: s.translucencyOption?.id ?? null,
    translucencyOptionLabel: s.translucencyOption?.label ?? null,
    blockSizeId: s.blockSize?.id ?? null,
    blockSizeLabel: s.blockSize?.label ?? null,
    manufacturingProcessId: s.manufacturingProcess?.id ?? null,
    manufacturingProcessLabel: s.manufacturingProcess?.label ?? null,
    characterizationOptionId: s.characterizationOption?.id ?? null,
    characterizationOptionLabel: s.characterizationOption?.label ?? null,
    finishingOptionId: s.finishingOption?.id ?? null,
    finishingOptionLabel: s.finishingOption?.label ?? null,
    teeth: s.teeth ?? "",
    priceCents: s.priceCents,
    quantidade: s.quantidade ?? 1,
    notes: s.notes,
    attachmentFileId: s.attachmentFile?.id ?? null,
    attachmentFileName: s.attachmentFile ? "Anexo" : null,
  }));
}
export type PrazoModo = "lab" | "seis" | "manual";

/** Parse "YYYY-MM-DD" como data local (evita shift de timezone do `new Date("YYYY-MM-DD")`, que
 *  interpreta como UTC meia-noite). */
function parseYmdLocal(ymd: string): Date {
  const [y, m, d] = ymd.split("-").map(Number);
  return new Date(y, (m ?? 1) - 1, d ?? 1);
}
